import { useEffect, useState } from "react";
import api, { errorMessage } from "../../lib/api";
import { errorInfo } from "../../lib/collaborators";
import {
  TERM_OPTIONS, buildCreatePayload, buildUpdatePayload, emptyForm, formFromMandate, hasChanges, isMandateForm, reasonOk, verificationRefusal,
} from "../../lib/mandates";
import CollaboratorPicker from "../CollaboratorPicker";
import EvidencePicker from "./EvidencePicker";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const nextDay = (day) => new Date(Date.parse(`${day}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);

// One place for the answers of the server: a stale version reloads, a business refusal speaks, anything else is the generic message.
async function send(request, { success, onDone, onConflict, setBusy }) {
  setBusy(true);
  try {
    const { data } = await request();
    toast.success(success);
    await onDone(data);
    return true;
  } catch (error) {
    const info = errorInfo(error);
    if (info.code === "version_conflict") {
      toast.error(info.data.msg);
      await onConflict();
    } else {
      toast.error(info.data?.msg || errorMessage(error));
    }
    return false;
  } finally {
    setBusy(false);
  }
}

function MandateFields({ values, onChange, types, typeLocked, features }) {
  const set = (patch) => onChange({ ...values, ...patch });
  return (
    <div className="space-y-5">
      {!typeLocked && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">Mandat</h3>
          <div className="space-y-2">
            <Label>Type de mandat *</Label>
            <Select value={values.type_id} onValueChange={(v) => set({ type_id: v })}>
              <SelectTrigger data-testid="select-mandate-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.filter((t) => t.active || t.id === values.type_id).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </section>
      )}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Dates</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2"><Label htmlFor="m-appointment">Date de l'acte de nomination</Label>
            <Input id="m-appointment" type="date" max={today()} className="text-base md:text-sm" value={values.appointment_date} onChange={(e) => set({ appointment_date: e.target.value })} data-testid="input-mandate-appointment" /></div>
          <div className="space-y-2"><Label htmlFor="m-start">Début des fonctions *</Label>
            <Input id="m-start" type="date" className="text-base md:text-sm" value={values.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="input-mandate-start" /></div>
        </div>
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Acte et pièces</h3>
        <div className="space-y-2"><Label htmlFor="m-act">Référence de l'acte</Label>
          <Input id="m-act" maxLength={200} placeholder="Ex. : procès-verbal d'assemblée générale du 10 janvier 2024" className="text-base md:text-sm" value={values.act_reference} onChange={(e) => set({ act_reference: e.target.value })} data-testid="input-mandate-act" /></div>
        <EvidencePicker value={values.evidence} onChange={(evidence) => set({ evidence })} features={features} />
        <p className="text-xs text-slate-500">Un brouillon se crée sans justificatif ; il en faut au moins un pour confirmer la vérification.</p>
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Durée</h3>
        <div className="space-y-2">
          <Label>Que disent les statuts ?</Label>
          <Select value={values.term_basis} onValueChange={(v) => set({ term_basis: v })}>
            <SelectTrigger data-testid="select-mandate-term"><SelectValue /></SelectTrigger>
            <SelectContent>{TERM_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {values.term_basis === "statutes_fixed" && (
          <div className="space-y-2"><Label htmlFor="m-due">Échéance prévue par les statuts *</Label>
            <Input id="m-due" type="date" className="text-base md:text-sm" value={values.term_due_date} onChange={(e) => set({ term_due_date: e.target.value })} data-testid="input-mandate-due" /></div>
        )}
        {values.term_basis === "statutes_silent" && (
          <p className="rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="mandate-term-hint">À défaut de disposition contraire des statuts, la durée est de quatre ans (article 324 de l'AUSCGIE). Le CRM s'en sert pour afficher une échéance indicative ; il ne termine ni ne renouvelle jamais un mandat.</p>
        )}
        <div className="space-y-2"><Label htmlFor="m-note">Remarque sur la durée</Label>
          <Input id="m-note" maxLength={300} className="text-base md:text-sm" value={values.term_note} onChange={(e) => set({ term_note: e.target.value })} /></div>
      </section>
    </div>
  );
}

// Création et correction. Une correction demande un motif et retire la vérification si elle touche ce que celle-ci couvre.
export function MandateFormDialog({ open, onOpenChange, fiche, mandate, types, features, onDone, onConflict }) {
  const editing = !!mandate;
  const [values, setValues] = useState(emptyForm());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues(mandate ? formFromMandate(mandate) : { ...emptyForm(), type_id: types.find((t) => t.active)?.id || "" });
    setReason("");
  }, [open, mandate, types]);

  const payload = editing ? buildUpdatePayload(mandate, values, reason) : null;
  const valid = isMandateForm(values, today(), { creating: !editing }) && (!editing || (hasChanges(payload) && reasonOk(reason)));
  const submit = async () => {
    const ok = await send(
      () => (editing ? api.patch(`/collaborators/mandates/${mandate.id}`, payload) : api.post(`/collaborators/${fiche.id}/mandates`, buildCreatePayload(values))),
      { success: editing ? "Mandat corrigé" : "Mandat enregistré", onDone, onConflict, setBusy },
    );
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="mandate-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Corriger le mandat" : "Ajouter un mandat"}</DialogTitle>
          <DialogDescription>{editing ? `Mandat « ${mandate.type.label} » de ${fiche.display_name}. La correction est conservée dans l'historique, avec son motif.` : `Mandat légal de ${fiche.display_name}. Il ne change ni son responsable, ni ses droits dans le CRM.`}</DialogDescription>
        </DialogHeader>
        <MandateFields values={values} onChange={setValues} types={types} typeLocked={editing} features={features} />
        {editing && (
          <div className="space-y-2">
            <Label htmlFor="m-reason">Motif de la correction *</Label>
            <Textarea id="m-reason" rows={2} maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-mandate-reason" />
            {mandate.verification === "verified" && <p className="text-xs text-amber-700" data-testid="mandate-withdraw-warning">Si vous modifiez les dates, l'acte, la durée ou les justificatifs, la vérification sera retirée.</p>}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="btn-mandate-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MandateEndDialog({ open, onOpenChange, mandate, onDone, onConflict }) {
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEndDate(today()); setReason(""); } }, [open]);
  const valid = !!endDate && !!mandate && endDate >= mandate.start_date && reasonOk(reason);
  const submit = async () => {
    const ok = await send(() => api.post(`/collaborators/mandates/${mandate.id}/end`, { expected_version: mandate.version, end_date: endDate, reason: reason.trim() }),
      { success: "Mandat terminé", onDone, onConflict, setBusy });
    if (ok) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mandate-end-dialog">
        <DialogHeader>
          <DialogTitle>Terminer le mandat</DialogTitle>
          <DialogDescription>À enregistrer quand la cessation est actée (démission, révocation, fin de mandat). Les formalités au registre du commerce restent à faire hors du CRM.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label htmlFor="e-date">Dernier jour du mandat *</Label>
            <Input id="e-date" type="date" min={mandate?.start_date} className="text-base md:text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="input-mandate-end-date" />
            <p className="text-xs text-slate-500">Une date à venir programme le départ : le mandat reste en cours jusque-là.</p></div>
          <div className="space-y-2"><Label htmlFor="e-reason">Motif *</Label>
            <Textarea id="e-reason" rows={2} maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-mandate-end-reason" /></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="btn-mandate-end-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Terminer le mandat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Succession : la période en cours se termine, la suivante s'ouvre dans la même opération — pour la même personne (renouvellement) ou une autre (passation).
export function MandateReplaceDialog({ open, onOpenChange, fiche, mandate, features, onDone, onConflict }) {
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [values, setValues] = useState(emptyForm());
  const [handover, setHandover] = useState(false);
  const [successor, setSuccessor] = useState({ id: null, name: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !mandate) return;
    setEndDate(today());
    setReason("");
    setHandover(false);
    setSuccessor({ id: null, name: "" });
    setValues({ ...emptyForm(), type_id: mandate.type.id, start_date: nextDay(today()) });
  }, [open, mandate]);

  const startsAfter = values.start_date && endDate && values.start_date > endDate;
  const valid = !!mandate && endDate >= mandate.start_date && reasonOk(reason) && startsAfter && isMandateForm(values, today(), { creating: false }) && (!handover || !!successor.id);
  const submit = async () => {
    const body = buildCreatePayload(values);
    delete body.type_id; // the successor has the same type: the server takes it from the mandate it replaces
    const ok = await send(() => api.post(`/collaborators/mandates/${mandate.id}/replace`, {
      expected_version: mandate.version, end_date: endDate, reason: reason.trim(), successor: { ...body, ...(handover ? { collaborator_id: successor.id } : {}) },
    }), { success: "Succession enregistrée", onDone, onConflict, setBusy });
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="mandate-replace-dialog">
        <DialogHeader>
          <DialogTitle>Renouveler ou transmettre le mandat</DialogTitle>
          <DialogDescription>Mandat « {mandate?.type.label} ». La période actuelle se termine et la suivante s'ouvre en une seule opération. L'ancienne période reste consultable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Fin de la période actuelle</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="r-end">Dernier jour *</Label>
                <Input id="r-end" type="date" min={mandate?.start_date} className="text-base md:text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="input-replace-end" /></div>
              <div className="space-y-2"><Label htmlFor="r-reason">Motif *</Label>
                <Input id="r-reason" maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-replace-reason" /></div>
            </div>
          </section>
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Période suivante</h3>
            <label className="flex min-h-11 items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={handover} onChange={(e) => setHandover(e.target.checked)} className="h-4 w-4" data-testid="check-replace-handover" />
              Transmettre à une autre personne
            </label>
            {handover ? (
              <div className="space-y-2"><Label>Nouveau titulaire *</Label>
                <CollaboratorPicker value={successor.id} label={successor.name} excludeId={fiche.id} placeholder="Choisir une personne"
                  onChange={(id, name) => setSuccessor({ id, name })} testid="picker-replace-successor" /></div>
            ) : (
              <p className="text-xs text-slate-500">Même personne : renouvellement.</p>
            )}
            {!startsAfter && values.start_date && <p className="text-xs text-red-600" data-testid="replace-start-hint">Le successeur commence après le dernier jour de la période actuelle.</p>}
            <MandateFields values={values} onChange={setValues} types={[{ id: mandate?.type.id, label: mandate?.type.label, active: true }]} typeLocked features={features} />
          </section>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!valid || busy} data-testid="btn-mandate-replace-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Enregistrer la succession</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Confirmer la vérification : un administrateur suffit. Le serveur dit ce qui manque si la demande est incomplète.
export function MandateVerifyDialog({ open, onOpenChange, mandate, onDone, onConflict }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);
  useEffect(() => { if (open) { setNote(""); setMissing(null); } }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/collaborators/mandates/${mandate.id}/verify`, { expected_version: mandate.version, note: note.trim() || null });
      toast.success("Vérification confirmée");
      await onDone();
      onOpenChange(false);
    } catch (error) {
      const info = errorInfo(error);
      const lacking = verificationRefusal(info);
      if (lacking) setMissing(lacking);
      else if (info.code === "version_conflict") { toast.error(info.data.msg); await onConflict(); onOpenChange(false); }
      else toast.error(info.data?.msg || errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mandate-verify-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer la vérification</DialogTitle>
          <DialogDescription>Vous attestez avoir contrôlé l'acte et les justificatifs joints. Votre nom et la date sont conservés ; un seul administrateur suffit.</DialogDescription>
        </DialogHeader>
        {missing && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="mandate-verify-missing">
            <p className="font-medium">Il manque encore :</p>
            <ul className="mt-1 list-disc pl-5">{missing.map((label) => <li key={label}>{label}</li>)}</ul>
          </div>
        )}
        <div className="space-y-2"><Label htmlFor="v-note">Remarque (facultative)</Label>
          <Textarea id="v-note" rows={2} maxLength={500} className="text-base md:text-sm" value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-mandate-verify-note" /></div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="btn-mandate-verify-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Confirmer la vérification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MandateUnverifyDialog({ open, onOpenChange, mandate, onDone, onConflict }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    const ok = await send(() => api.post(`/collaborators/mandates/${mandate.id}/unverify`, { expected_version: mandate.version, reason: reason.trim() }),
      { success: "Vérification retirée", onDone, onConflict, setBusy });
    if (ok) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mandate-unverify-dialog">
        <DialogHeader>
          <DialogTitle>Retirer la vérification</DialogTitle>
          <DialogDescription>Le mandat reste en cours et visible, mais il n'est plus présenté comme vérifié.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2"><Label htmlFor="u-reason">Motif *</Label>
          <Textarea id="u-reason" rows={2} maxLength={200} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-mandate-unverify-reason" /></div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={!reasonOk(reason) || busy} data-testid="btn-mandate-unverify-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Retirer la vérification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
